import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { tryLeave } from "@/floor/leaveGuard";
import { Loader2, LayoutDashboard, Warehouse } from "lucide-react";

// Desk ↔ Floor surface toggle. The chosen surface is saved SERVER-SIDE per user
// (never localStorage) so it follows the tech to any device, and becomes their
// default landing after login.
export function SurfaceToggle({ compact = false }) {
  const { user, setSurface, has } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Floor-locked accounts have no Desk surface — hide the toggle entirely (server also 403s).
  if (!has("use_desk_surface")) return null;
  const onFloor = user?.preferred_surface === "floor";
  const next = onFloor ? "desk" : "floor";
  const label = onFloor ? "Desk View" : "Floor View";
  const Icon = onFloor ? LayoutDashboard : Warehouse;

  const go = async () => {
    if (busy) return;
    // A loaded put-away location asks first — nothing is lost, but nobody walks off mid-run.
    if (!tryLeave(null)) return;
    setBusy(true);
    try {
      await setSurface(next);
      navigate(next === "floor" ? "/floor" : "/home");
    } finally { setBusy(false); }
  };

  if (compact) {
    return (
      <button type="button" data-testid="surface-toggle" onClick={go} disabled={busy}
        className="flex items-center gap-1.5 rounded-lg h-8 px-2 sm:px-3 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold transition-colors">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  }
  return (
    <button type="button" data-testid="surface-toggle" onClick={go} disabled={busy}
      className="flex items-center gap-2.5 rounded-xl h-14 px-5 bg-[#0F172A] text-white font-bold text-lg active:scale-95 transition-transform duration-100 disabled:opacity-60">
      {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Icon className="w-6 h-6" />}
      {label}
    </button>
  );
}
